import { Select, Skeleton, createListCollection } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { type CatalogPublic, LibraryService } from '@/client';

function getCatalogsQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['catalogs'],
    queryFn: async () => await LibraryService.readLibraryCatalogs({ companySlug: slug }),
  };
}

export const SelectCatalogs = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: CatalogPublic[]) => void;
}) => {
  const { slug } = useParams({ strict: false });
  const { i18n } = useTranslation();
  const { data: catalogsData, isLoading } = useQuery({
    ...getCatalogsQueryOptions({ slug }),
  });

  const catalogsCollection = createListCollection({
    items: [
      {
        id: 'None',
        name: i18n.t('suggestion.filter.general_catalog'),
      },
    ].concat(
      catalogsData?.data.map((catalog) => ({
        id: catalog.id,
        name: catalog.name,
      })) || []
    ),
    itemToString: (item: CatalogPublic) => item.name,
    itemToValue: (item: CatalogPublic) => item.id,
  });

  return (
    <>
      {isLoading ? (
        <Skeleton height="8" />
      ) : (
        <Select.Root
          multiple
          collection={catalogsCollection}
          size="sm"
          value={value}
          onValueChange={(e) => onChange(catalogsCollection.items.filter((el) => e.value.includes(el.id)))}
          positioning={{ sameWidth: true }}
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select catalog" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {catalogsCollection.items.map((catalog: CatalogPublic) => (
                <Select.Item item={catalog} key={catalog.id} justifyContent="space-between">
                  {catalog.name}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      )}
    </>
  );
};

export default SelectCatalogs;
